import React from "react";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import { getLocalDateString } from "../../../utils/dateHelpers";

const DatePickerInput = ({
  selected,
  onChange,
  placeholder = "Select date",
  maxDate = new Date(),
  minDate,
  disabled = false,
  error = null,
  label = null,
  required = false,
  className = "",
  activityCountsByDate = {},
}) => {
  // Determine CSS class for days with activities
  const getDayClassName = (date) => {
    if (!date) return "";
    const dateStr = getLocalDateString(date);
    const count = activityCountsByDate[dateStr] || 0;

    return count > 0 ? "has-activity" : "";
  };

  // Custom day renderer for tooltip title
  const renderDayContents = (dayOfMonth, date) => {
    if (!date) return dayOfMonth;
    const dateStr = getLocalDateString(date);
    const count = activityCountsByDate[dateStr] || 0;

    if (count > 0) {
      return (
        <span title={`${count} activity ${count === 1 ? "entry" : "entries"} logged on ${dateStr}`}>
          {dayOfMonth}
        </span>
      );
    }

    return dayOfMonth;
  };

  return (
    <div className={`flex flex-col gap-1.5 w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative w-full">
        <DatePicker
          selected={selected}
          onChange={onChange}
          maxDate={maxDate}
          minDate={minDate}
          disabled={disabled}
          placeholderText={placeholder}
          dateFormat="dd/MM/yyyy"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          wrapperClassName="w-full"
          dayClassName={getDayClassName}
          renderDayContents={renderDayContents}
          className={`
            w-full px-4 py-2.5 pr-10
            text-sm text-slate-900 placeholder-slate-400
            bg-white border rounded-xl
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
            disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
            ${error ? "border-red-300 focus:ring-red-500" : "border-slate-300 hover:border-slate-400"}
          `}
        />
        <Calendar className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
      </div>

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}

      <style>{`
        .react-datepicker-wrapper {
          width: 100% !important;
          display: block !important;
        }
        .react-datepicker-popper {
          z-index: 50 !important;
        }
        .react-datepicker__day.has-activity {
          background-color: #3b82f6 !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          border-radius: 0.375rem !important;
        }
        .react-datepicker__day.has-activity:hover {
          background-color: #2563eb !important;
          color: #ffffff !important;
        }
        .react-datepicker__day--selected.has-activity,
        .react-datepicker__day--selected {
          background-color: #0f172a !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          border-radius: 0.375rem !important;
        }
      `}</style>
    </div>
  );
};

export default DatePickerInput;
